// Based on:
//     Underscore.js 1.8.3
//     http://underscorejs.org
//     (c) 2009-2015 Jeremy Ashkenas, DocumentCloud and Investigative Reporters & Editors
//     Underscore may be freely distributed under the MIT license.


/*
 Underscore Lite (low-weight, low-impact version of the real Underscore)

 Author's (hacker's) name: Anders Gissel <agi@dis-play.dk>
 ************************************************************************************************************

 The purpose of this file is to provide the basic Underscore-functionality used in the core of Frontline v3, without
 having to load and parse the entirety of Underscore (which might not be used on the rest of the site). If you don't
 use underscore, you don't have to worry about this file at all.

 If you DO use the other functionality in Underscore, you have three choices:

    1) Load the complete Underscore-library in your code, and just live with the tiny extra space
       taken up by this file. A bit messy, but should work just fine.

    2) Add your own functions to the end of this file, copying them more or less directly from the original
       Underscore source. Remember to include the various helper functions, and to check for missing references
       and similar.

    3) "Polyfill" your functions on top of this file's return object, like so:

            // utils/underscoreLite.horse.js
            import underscoreLite from './underscoreLite';
            underscoreLite.horse = function() {
                window.console.log("A pony is a tiny horse.");
            };
            export default underscoreLite;


       And inside your "target file" (dumb example, but still):

            // main.js, or whatever
            import underscoreLite from './utils/underscoreLite';
            import './utils/underscoreLite.horse';

            underscoreLite.horse();
            underscoreLite.forEach([1,2,3], n => window.console.log(n));


       Thanks to the object reference handling in JS, that's perfectly safe, and works like a charm.

       If the functions you need rely on helper functions and similar, some, if not all, of them have already
       been exported as functions and const's from this file.

       All your "polyfills" will be accessible everywhere once you've included them just once in a single file,
       thanks to the dependency injection handling in Bublé / Rollup. However, you should still remember
       to import all your needed polyfills in the files that need them, in case you stop using one of those files
       at a later stage. So you should probably end up with something like this:

            import underscoreLite from './utils/underscoreLite';
            import './utils/underscoreLite.a';
            import './utils/underscoreLite.b';
            import './utils/underscoreLite.c';
            import './utils/underscoreLite.d';

            underscoreLite.d();



 */



// Create a safe reference to the Underscore (lite) object for use below.
const UnderscoreLite = function(obj) {
    if (obj instanceof UnderscoreLite) {
        return obj;
    }
    if (!(this instanceof UnderscoreLite)) {
        return new UnderscoreLite(obj);
    }
};





// Save bytes in the minified (but not gzipped) version:
export const ArrayProto = Array.prototype;
export const ObjProto = Object.prototype;
export const FuncProto = Function.prototype;


export const hasEnumBug = !{toString: null}.propertyIsEnumerable('toString');
export const nonEnumerableProps = ['valueOf', 'isPrototypeOf', 'toString', 'propertyIsEnumerable', 'hasOwnProperty', 'toLocaleString'];

// All **ECMAScript 5** native function implementations that we hope to use
// are declared here.
export const nativeKeys         = Object.keys;
export const nativeIsArray      = Array.isArray;
export const nativeBind         = FuncProto.bind;
export const nativeCreate       = Object.create;


// Create quick reference variables for speed access to core prototypes.
export const push             = ArrayProto.push;
export const slice            = ArrayProto.slice;
export const toString         = ObjProto.toString;
/*jshint ignore:start */
// For some reason this is flagged as a redefinition, but it isn't. Might be a bug in JSHint,
// but ignoring it should be safe.
export const hasOwnProperty   = ObjProto.hasOwnProperty;
/*jshint ignore:end */


// Shortcut function for checking if an object has a given property directly
// on itself (in other words, not on a prototype).
UnderscoreLite.has = function(obj, key) {
    return obj !== null && hasOwnProperty.call(obj, key);
};


// Is a given variable an object?
UnderscoreLite.isObject = function(obj) {
    let type = typeof obj;
    return type === 'function' || type === 'object' && !!obj;
};


export function collectNonEnumProps(obj, keys) {
    let nonEnumIdx = nonEnumerableProps.length;
    let constructor = obj.constructor;
    let proto = (UnderscoreLite.isFunction(constructor) && constructor.prototype) || ObjProto;

    // Constructor is a special case.
    let prop = 'constructor';
    if (UnderscoreLite.has(obj, prop) && !UnderscoreLite.contains(keys, prop)) {
        keys.push(prop);
    }

    while (nonEnumIdx) {
        nonEnumIdx -= 1;
        prop = nonEnumerableProps[nonEnumIdx];
        if (prop in obj && obj[prop] !== proto[prop] && !UnderscoreLite.contains(keys, prop)) {
            keys.push(prop);
        }
    }
}




// Internal function that returns an efficient (for current engines) version
// of the passed-in callback, to be repeatedly applied in other Underscore
// functions.
export function optimizeCb(func, context, argCount) {
    if (context === void 0) {
        return func;
    }
    switch (argCount === null ? 3 : argCount) {
    case 1: return function(value) {
        return func.call(context, value);
    };
    case 2: return function(value, other) {
        return func.call(context, value, other);
    };
    case 3: return function(value, index, collection) {
        return func.call(context, value, index, collection);
    };
    case 4: return function(accumulator, value, index, collection) {
        return func.call(context, accumulator, value, index, collection);
    };
    }
    return function() {
        return func.apply(context, arguments);
    };
}

export function property(key) {
    return function(obj) {
        return obj === null ? void 0 : obj[key];
    };
}


// Helper for collection methods to determine whether a collection
// should be iterated as an array or as an object
// Related: http://people.mozilla.org/~jorendorff/es6-draft.html#sec-tolength
// Avoids a very nasty iOS 8 JIT bug on ARM-64. #2094
let MAX_ARRAY_INDEX = Math.pow(2, 53) - 1;
let getLength = property('length');
export function isArrayLike(collection) {
    let length = getLength(collection);
    return typeof length === 'number' && length >= 0 && length <= MAX_ARRAY_INDEX;
}


// Naked function reference for surrogate-prototype-swapping.
export const Ctor = function(){};


// An internal function for creating assigner functions.
export function createAssigner(keysFunc, undefinedOnly) {
    return function(obj) {
        let length = arguments.length;
        if (length < 2 || obj === null) {
            return obj;
        }
        for (let index = 1; index < length; index += 1) {
            let source = arguments[index],
                keys = keysFunc(source),
                l = keys.length;
            for (let i = 0; i < l; i += 1) {
                let key = keys[i];
                if (!undefinedOnly || obj[key] === void 0) {
                    obj[key] = source[key];
                }
            }
        }
        return obj;
    };
}


// An internal function for creating a new object that inherits from another.
export function baseCreate(prototype) {
    if (!UnderscoreLite.isObject(prototype)) {
        return {};
    }
    if (nativeCreate) {
        return nativeCreate(prototype);
    }
    Ctor.prototype = prototype;
    let result = new Ctor();
    Ctor.prototype = null;
    return result;
}




// A mostly-internal function to generate callbacks that can be applied
// to each element in a collection, returning the desired result — either
// identity, an arbitrary callback, a property matcher, or a property accessor.
export function cb(value, context, argCount) {
    if (value === null) {
        return UnderscoreLite.identity;
    }
    if (UnderscoreLite.isFunction(value)) {
        return optimizeCb(value, context, argCount);
    }
    if (UnderscoreLite.isObject(value)) {
        return UnderscoreLite.matcher(value);
    }
    return UnderscoreLite.property(value);
}



// Retrieve all the property names of an object.
UnderscoreLite.allKeys = function(obj) {
    if (!UnderscoreLite.isObject(obj)) {
        return [];
    }
    let keys = [];
    /*jshint ignore:start */
    for (let key in obj) {
        keys.push(key);
    }
    /*jshint ignore:end */
    // Ahem, IE < 9.
    if (hasEnumBug) {
        collectNonEnumProps(obj, keys);
    }
    return keys;
};


// A (possibly faster) way to get the current timestamp as an integer.
UnderscoreLite.now = Date.now || function() {
    return new Date().getTime();
};



// Optimize `isFunction` if appropriate. Work around some typeof bugs in old v8,
// IE 11 (#1621), and in Safari 8 (#1929).
if (typeof /./ !== 'function' && typeof Int8Array !== 'object') {
    UnderscoreLite.isFunction = function(obj) {
        return typeof obj === 'function' || false;
    };
}



// Retrieve the names of an object's own properties.
// Delegates to **ECMAScript 5**'s native `Object.keys`
UnderscoreLite.keys = function(obj) {
    if (!UnderscoreLite.isObject(obj)) {
        return [];
    }
    if (nativeKeys) {
        return nativeKeys(obj);
    }
    let keys = [];
    for (let key in obj) {
        if (UnderscoreLite.has(obj, key)) {
            keys.push(key);
        }
    }
    // Ahem, IE < 9.
    if (hasEnumBug) {
        collectNonEnumProps(obj, keys);
    }
    return keys;
};




// Extend a given object with all the properties in passed-in object(s).
UnderscoreLite.extend = createAssigner(UnderscoreLite.allKeys);

// Assigns a given object with all the own properties in the passed-in object(s)
// (https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Object/assign)
UnderscoreLite.extendOwn = UnderscoreLite.assign = createAssigner(UnderscoreLite.keys);



// Returns a predicate for checking whether an object has a given set of
// `key:value` pairs.
UnderscoreLite.matcher = UnderscoreLite.matches = function(attrs) {
    attrs = UnderscoreLite.extendOwn({}, attrs);
    return function(obj) {
        return UnderscoreLite.isMatch(obj, attrs);
    };
};




// Collection Functions
// --------------------

// The cornerstone, an `each` implementation, aka `forEach`.
// Handles raw objects in addition to array-likes. Treats all
// sparse array-likes as if they were dense.
UnderscoreLite.each = UnderscoreLite.forEach = function(obj, iteratee, context) {
    iteratee = optimizeCb(iteratee, context);
    let i, length;
    if (isArrayLike(obj)) {
        for (i = 0, length = obj.length; i < length; i += 1) {
            iteratee(obj[i], i, obj);
        }
    } else {
        let keys = UnderscoreLite.keys(obj);
        for (i = 0, length = keys.length; i < length; i += 1) {
            iteratee(obj[keys[i]], keys[i], obj);
        }
    }
    return obj;
};






// Return all the elements that pass a truth test.
// Aliased as `select`.
UnderscoreLite.filter = UnderscoreLite.select = function(obj, predicate, context) {
    let results = [];
    predicate = cb(predicate, context);
    UnderscoreLite.each(obj, function(value, index, list) {
        if (predicate(value, index, list)) {
            results.push(value);
        }
    });
    return results;
};





// Returns a function, that, when invoked, will only be triggered at most once
// during a given window of time. Normally, the throttled function will run
// as much as it can, without ever going more than once per `wait` duration;
// but if you'd like to disable the execution on the leading edge, pass
// `{leading: false}`. To disable execution on the trailing edge, ditto.
UnderscoreLite.throttle = function(func, wait, options) {
    let context, args, result;
    let timeout = null;
    let previous = 0;
    if (!options) {
        options = {};
    }
    let later = function() {
        previous = options.leading === false ? 0 : UnderscoreLite.now();
        timeout = null;
        result = func.apply(context, args);
        if (!timeout) {
            context = args = null;
        }
    };
    return function() {
        let now = UnderscoreLite.now();
        if (!previous && options.leading === false) {
            previous = now;
        }
        let remaining = wait - (now - previous);
        context = this;
        args = arguments;
        if (remaining <= 0 || remaining > wait) {
            if (timeout) {
                clearTimeout(timeout);
                timeout = null;
            }
            previous = now;
            result = func.apply(context, args);
            if (!timeout) {
                context = args = null;
            }
        } else if (!timeout && options.trailing !== false) {
            timeout = setTimeout(later, remaining);
        }
        return result;
    };
};




export default UnderscoreLite;